import { Eye, Target, Crosshair, Award, Users, Lightbulb, Leaf } from "lucide-react";
import { FadeUp, SectionHeading, CTASection } from "../components/Shared";
import { IMAGES } from "../lib/brand";

const PILLARS = [
  { icon: Eye, title: "Vision", desc: "To become the most trusted digital manufacturing platform — where any engineer, anywhere, can turn a design into a precision part without friction." },
  { icon: Target, title: "Mission", desc: "Digitize and de-risk custom manufacturing by combining engineering expertise, a vetted supplier network and intelligent software into one seamless workflow." },
  { icon: Crosshair, title: "Focus", desc: "Quality-critical industries: industrial equipment, energy, automotive, electronics and infrastructure — where precision and traceability are non-negotiable." },
];

const VALUES = [
  { icon: Award, title: "Uncompromising Quality", desc: "Documented inspection and traceability on every single order." },
  { icon: Users, title: "Customer Obsession", desc: "We win when your parts arrive on time and in spec." },
  { icon: Lightbulb, title: "Engineering First", desc: "Decisions driven by data, tolerances and manufacturability — not guesswork." },
  { icon: Leaf, title: "Responsible Growth", desc: "Efficient logistics and local sourcing to reduce waste and lead times." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="py-28 sm:py-36 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary mb-6 font-mono-tech">About Vectron Global LLP</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-4xl leading-[1.05]">
              We're building the <span className="font-medium text-primary">infrastructure of modern manufacturing.</span>
            </h1>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <FadeUp>
            <div className="rounded-xl overflow-hidden border border-border">
              <img src={IMAGES.engineersBlueprint} alt="Vectron engineers reviewing designs" className="w-full h-[460px] object-cover" />
            </div>
          </FadeUp>
          <div className="space-y-10">
            {PILLARS.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.1}>
                <div className="flex gap-6" data-testid={`about-pillar-${p.title.toLowerCase()}`}>
                  <div className="h-14 w-14 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <p.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-medium font-heading">{p.title}</h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-muted/60 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHeading overline="Core Values" title="The principles behind every part we ship." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.08}>
                <div className="h-full p-7 rounded-xl bg-card border border-border transition-transform duration-200 hover:-translate-y-1" data-testid={`value-card-${i}`}>
                  <v.icon className="h-6 w-6 text-accent mb-5" strokeWidth={1.5} />
                  <h4 className="text-lg font-semibold font-heading">{v.title}</h4>
                  <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Let's build something together."
        description="Whether you need parts made or want to join our network — we'd love to hear from you."
      />
    </div>
  );
}
